import Vue from 'vue'
import { mapGetters, mapState } from 'vuex'
import { Blockchain, BLOCKCHAIN_OPTIONS, BlockchainNames } from '@/constants/blockchain'
import { RoomTypes } from '@/constants/chat/RoomTypes'
import { CURRENCY_FIELD_BACKEND_ID } from '@/constants/currency'
import { JobApplicationStatuses } from '@/constants/job/jobApplicationStatuses'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import {
  STAGE_BLOCKED_BY_FREELANCER,
  STAGE_COMPLETED,
  STAGE_DEADLINE_OVERDUE,
  STAGE_DISPUTED,
  STAGE_IN_PROGRESS,
  STAGE_NEW,
  STAGE_STARTED,
} from '@/constants/job/jobStages'
import { JOB_CHAT, JOB_DETAILS, JOB_EDIT } from '@/constants/routes'
import { DATE_TIME_FORMAT_BY_MERIDIEM_HUMAN } from '@/constants/utils/date'
import ChatRoom from '@/models-ts/chat/ChatRoom'
import JobApplication from '@/models-ts/job/JobApplication'
import JobApplicationStatusBadge from '@/partials/StatusBadges/JobApplicationStatusBadge/JobApplicationStatusBadge.vue'
import JobStatusBadge from '@/partials/StatusBadges/JobStatusBadge/JobStatusBadge.vue'
import UserInfo from '@/partials/UserInfo/UserInfo.vue'
import { RootState } from '@/store'
import { getTxLink } from '@/utils-ts/transaction'
import { convertToUsd, getCurrency } from '@/utils/currency'
import { addDays, formatDate, isBefore } from '@/utils/date'
import { formatCurrency } from '@/utils/moneyFormat'
import CardActionMenu from '../../components/CardActionMenu/CardActionMenu.vue'
import JobTypeBadge from '../../components/JobTypeBadge/JobTypeBadge.vue'

export default Vue.extend<any, any, any, any>({
  components: {
    CardActionMenu,
    UserInfo,
    JobTypeBadge,
    JobStatusBadge,
    JobApplicationStatusBadge,
  },
  props: {
    application: JobApplication,
  },
  data() {
    return {
      JOB_DETAILS,
      BlockchainNames,
      DATE_TIME_FORMAT_BY_MERIDIEM_HUMAN,
      JOB_EDIT,
      publishing: false,
    }
  },
  computed: {
    ...mapState<RootState>({
      userId: (state: RootState) => state.user?.id,
      rates: (state: RootState) => state.rates.rates.value,
    }),
    ...mapGetters({
      getUnreadCount: 'chatNew/getUnreadCount',
    }),
    chatRoomId() {
      return ChatRoom.generateRoomId({
        type: RoomTypes.JOB,
        applicationId: this.application.id,
        freelancerId: this.application.freelancer.id,
        customerId: this.application.customer.id,
      })
    },
    chatLink() {
      return {
        name: JOB_CHAT,
        params: { id: this.application.job.id, slug: this.application.job.slug },
        query: { room: this.chatRoomId },
      }
    },
    chatFeedbackLink() {
      return {
        ...this.chatLink,
        query: {
          ...this.chatLink.query,
          action: 'feedback',
        },
      }
    },
    isLocked() {
      return this.room && !this.room.isUnlocked
    },
    unreadMsgs() {
      return this.getUnreadCount(this.chatRoomId)
    },
    hasChat() {
      return this.application.id
    },
    hasReview() {
      return this.isCompleted && this.application.freelancerLeftReview
    },
    isStarted() {
      return ![STAGE_NEW, STAGE_STARTED].includes(this.application.job.stage)
    },
    amISelectedFreelancer() {
      return this.application.selectedFreelancerId === this.userId
    },
    isInProgress() {
      return (
        this.application.status === JobApplicationStatuses.NEW &&
        [STAGE_IN_PROGRESS, STAGE_BLOCKED_BY_FREELANCER].includes(this.application.job.stage)
      )
    },
    isOfferDeclined() {
      return [JobOfferStages.DECLINED_BY_CUSTOMER, JobOfferStages.DECLINED_BY_FREELANCER].includes(this.application.offer.stage)
    },
    isApplication() {
      return [STAGE_NEW, STAGE_STARTED].includes(this.application.job.stage) || this.isOfferDeclined
    },
    isArchived() {
      return (
        this.application.status === JobApplicationStatuses.ARCHIVED &&
        [STAGE_NEW, STAGE_STARTED, STAGE_IN_PROGRESS, STAGE_BLOCKED_BY_FREELANCER].includes(this.application.job.stage)
      )
    },
    isCompleted() {
      return (
        this.amISelectedFreelancer &&
        [STAGE_COMPLETED, STAGE_DISPUTED, STAGE_DEADLINE_OVERDUE].includes(this.application.job.stage) &&
        !this.isOfferDeclined
      )
    },
    isMember() {
      return this.isStarted && !this.isArchived
    },
    primaryBudget() {
      if (!this.isMember) {
        return formatCurrency(this.application.budget, { currency: 'USDT' })
      }
      const budget = this.application.budget
      const currency = getCurrency({
        blockchain: this.application.job.blockchain,
        value: this.application.job.currency,
        field: CURRENCY_FIELD_BACKEND_ID,
      })
      return this.isStarted ? `${budget} ${currency?.name}` : `$${budget}`
    },
    secondaryBudget() {
      if (this.isMember) {
        return `$${convertToUsd({
          value: this.application.job.escrowBalance,
          currency: this.application.job.currency,
          rates: this.rates,
        })}`
      }
    },
    transactionStartLink() {
      return (
        this.application.job.txIdCreated &&
        getTxLink({
          blockchain: this.application.job.blockchain,
          tx: this.application.job.txIdCreated,
        })
      )
    },
    transactionEndLink() {
      return (
        this.application.job.txIdCompleted &&
        getTxLink({
          blockchain: this.application.job.blockchain,
          tx: this.application.job.txIdCompleted,
        })
      )
    },
    completedDate() {
      return this.application.job.completedAt && formatDate(this.application.job.completedAt, DATE_TIME_FORMAT_BY_MERIDIEM_HUMAN)
    },
    deadline() {
      return (
        this.application.job.inProgressAt &&
        formatDate(addDays(this.application.job.inProgressAt, this.application.job.estimate), DATE_TIME_FORMAT_BY_MERIDIEM_HUMAN)
      )
    },
    deadlineHasCome() {
      return isBefore(this.deadline, new Date())
    },
    hasDisputeBtn() {
      return this.application.job.stage === STAGE_IN_PROGRESS && this.deadlineHasCome
    },
    hasReviewBtn() {
      return this.isCompleted && !this.application.freelancerLeftReview
    },
    chatDisputeLink() {
      return {
        ...this.chatLink,
        query: {
          ...this.chatLink.query,
          action: 'dispute',
        },
      }
    },
    appDate() {
      return this.isApplication && formatDate(this.application.createdAt, DATE_TIME_FORMAT_BY_MERIDIEM_HUMAN)
    },
    goToLink() {
      return { name: JOB_DETAILS, params: { id: this.application.job.id, slug: this.application.job.slug } }
    },
    cardLink() {
      return this.hasChat ? this.chatLink : { name: JOB_DETAILS, params: { id: this.application.job.id, slug: this.application.job.slug } }
    },
    deadlineDaysFormatted() {
      return this.application.job.estimate
        ? `${this.application.job.estimate} ${this.application.job.estimate === 1 ? 'day' : 'days'}`
        : 'TBD'
    },
  },
  methods: {
    onChatClick() {
      this.$router.push(this.chatLink)
    },
    onLeaveReviewClick() {
      this.$router.push(this.chatFeedbackLink)
    },
    getBlockchainLabel(blockchain: Blockchain) {
      return BLOCKCHAIN_OPTIONS.find((bl) => bl.value === blockchain)?.name || ''
    },
  },
})
